import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API_ELSTIC_URL } from "../../config";

export const useOrderForm = () => {
  // Basic Form State
  const [formData, setFormData] = useState({
    user_id: "",
    plan_id: "",
    plan_type: "",
    plan_price: 0,
    total_amount: 0,
    final_amount: 0,
    payment_status: "link",
    payment_mode: "",
    amount_received: "",
    bank_name: "",
    utr_no: "",
    payment_attachment: null,
    billing_address: "",
    razor_pay_order_id: "",
    is_attorney: "",
    customer_type: "",
    end_date: "",
    company_name: "",
    gstNumber: "",
    pincode: "",
    state_name: "",
    total_tm_count: 0,
    gst_inclusion: false,
  });

  // Order Summary State
  const [orderSummary, setOrderSummary] = useState({
    customerName: "-",
    customerEmail: "-",
    customerMobile: "-",
    customerType: "-",
    planName: "-",
    planType: "-",
    planAmount: 0,
    totalTMCount: 0,
    finalAmount: 0,
    gstAmount: 0,
    gstRate: 18,
    totalAmount: 0,
    includeGST: true,
    attorneyDetails: [],
    end_date: "-",
  });

  // UI States
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [editablePlanAmount, setEditablePlanAmount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingAttorney, setIsCheckingAttorney] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Data States
  const [customerList, setCustomerList] = useState([]);
  const [states, setStates] = useState([]);
  const [errors, setErrors] = useState({});
  const [customerErrors, setCustomerErrors] = useState({});

  // Attorney States
  const [attorneyCount, setAttorneyCount] = useState(0);
  const [attorneyNumbers, setAttorneyNumbers] = useState([]);
  const [attorneyValidation, setAttorneyValidation] = useState({});
  const [attorneyValidationMessages, setAttorneyValidationMessages] = useState(
    {},
  );
  const [attorneyTMCounts, setAttorneyTMCounts] = useState({});

  return {
    formData,
    setFormData,
    orderSummary,
    setOrderSummary,
    isEditingAmount,
    setIsEditingAmount,
    editablePlanAmount,
    setEditablePlanAmount,
    isSubmitting,
    setIsSubmitting,
    isCheckingAttorney,
    setIsCheckingAttorney,
    isGenerating,
    setIsGenerating,
    isLoading,
    setIsLoading,
    customerList,
    setCustomerList,
    states,
    setStates,
    errors,
    setErrors,
    customerErrors,
    setCustomerErrors,
    attorneyCount,
    setAttorneyCount,
    attorneyNumbers,
    setAttorneyNumbers,
    attorneyValidation,
    setAttorneyValidation,
    attorneyValidationMessages,
    setAttorneyValidationMessages,
    attorneyTMCounts,
    setAttorneyTMCounts,
  };
};

export const useOrderCalculations = (
  formData,
  activePlans,
  customerList,
  isEditingAmount,
  editablePlanAmount,
  attorneyNumbers,
  attorneyTMCounts,
  attorneyValidation,
  editOrderData,
) => {
  const safeNumber = (value) => (isNaN(Number(value)) ? 0 : Number(value));

  const calculateOrderSummary = useCallback(() => {
    const {
      plan_id,
      plan_type,
      plan_price,
      customer_type,
      user_id,
      end_date,
      gst_inclusion,
      is_attorney,
    } = formData;

    if (customer_type === "2") {
      const selectedUser = customerList.find(
        (user) => user.id === Number(user_id),
      );

      let selectedPlan = activePlans.find(
        (plan) => plan.id === Number(plan_id),
      );

      let totalTMCount = Object.values(attorneyTMCounts).reduce(
        (sum, count) => sum + Number(count || 0),
        0,
      );
      if (is_attorney === "0") {
        totalTMCount = 5;
      }

      const attorneyDetails = attorneyNumbers
        .filter((num) => num.trim() !== "")
        .map((num) => ({
          attorneyNumber: num,
          tmCount: attorneyTMCounts[num] || 0,
          status: attorneyValidation[num] || "pending",
        }));

      return {
        customerName: selectedUser?.name || "-",
        customerEmail: selectedUser?.email || "-",
        customerMobile: selectedUser?.mobile || "-",
        customerType: "Free Trial",
        planName: selectedPlan?.name || "-",
        planType: "-",
        planAmount: 0,
        totalTMCount: totalTMCount,
        finalAmount: 0,
        gstAmount: 0,
        gstRate: 18,
        totalAmount: 0,
        includeGST: gst_inclusion,
        attorneyDetails: attorneyDetails,
        end_date: end_date || "-",
      };
    }

    const includeGST = gst_inclusion;

    let planAmount = isEditingAmount
      ? editablePlanAmount
      : plan_price
        ? Number(plan_price)
        : 0;

    if (!isEditingAmount && !plan_price && plan_id && plan_type) {
      const selectedPlan = activePlans.find(
        (plan) => plan.id === Number(plan_id),
      );
      if (selectedPlan) {
        planAmount =
          plan_type === "monthly"
            ? Number(selectedPlan.monthly_price)
            : Number(selectedPlan.annual_price);
      }
    }

    const attorneyDetails = attorneyNumbers
      .filter((num) => num.trim() !== "")
      .map((num) => ({
        attorneyNumber: num,
        tmCount: attorneyTMCounts[num] || 0,
        status: attorneyValidation[num] || "pending",
      }));

    let totalTMCount = Object.values(attorneyTMCounts).reduce(
      (sum, count) => sum + Number(count || 0),
      0,
    );

    if (is_attorney === "0" && totalTMCount === 0) {
      totalTMCount = 1;
    }

    const finalAmount =
      totalTMCount > 0 ? planAmount * totalTMCount : planAmount;

    const gstAmount = includeGST ? finalAmount * 0.18 : 0;
    const totalAmount = finalAmount + gstAmount;

    const selectedUser = customerList.find(
      (user) => user.id === Number(user_id),
    );
    let selectedPlan = activePlans.find((plan) => plan.id === Number(plan_id));

    if (!selectedPlan && editOrderData?.data?.plan_name) {
      selectedPlan = {
        name: editOrderData.data.plan_name,
      };
    }

    return {
      customerName: selectedUser?.name || "-",
      customerEmail: selectedUser?.email || "-",
      customerMobile: selectedUser?.mobile || "-",
      customerType:
        customer_type === "2"
          ? "Free Trial"
          : customer_type === "1"
            ? "Paid"
            : "-",
      planName: selectedPlan?.name || "-",
      planType:
        plan_type === "monthly"
          ? "Monthly"
          : plan_type === "annualy"
            ? "Annual"
            : "-",
      planAmount: Number(planAmount).toFixed(2),
      totalTMCount: totalTMCount,
      finalAmount: Number(finalAmount).toFixed(2),
      gstAmount: Number(gstAmount).toFixed(2),
      gstRate: 18,
      totalAmount: Number(totalAmount).toFixed(2),
      includeGST: includeGST,
      attorneyDetails: attorneyDetails,
      end_date: end_date || "-",
    };
  }, [
    formData.plan_id,
    formData.plan_type,
    formData.plan_price,
    formData.customer_type,
    formData.user_id,
    formData.end_date,
    formData.gst_inclusion,
    formData.is_attorney,
    editablePlanAmount,
    isEditingAmount,
    attorneyTMCounts,
    attorneyNumbers,
    attorneyValidation,
    activePlans,
    customerList,
  ]);

  return { calculateOrderSummary, safeNumber };
};

/**
 * Unified attorney validation hook.
 *
 * FIX: Uses valid_attorneys / invalid_attorneys arrays from the API response
 * as the source of truth for EACH attorney individually.
 *
 * Previous bug: When API returned 409 "Already Exist" for attorney 1356,
 * the code was marking ALL attorneys (including valid 1344) as invalid
 * because it defaulted all to invalid and only overrode from the `details`
 * array — which is empty in the "Already Exist" response.
 *
 * Now: Each attorney is checked against valid_attorneys and invalid_attorneys
 * independently. If an attorney is in neither array (edge case), it's marked
 * as "already assigned".
 */
export const useAttorneyValidation = (
  setIsCheckingAttorney,
  setAttorneyValidation,
  setAttorneyValidationMessages,
  setAttorneyTMCounts,
  calculateOrderSummary,
  setAttorneyNameOptions,
) => {
  /**
   * Process API response — shared between try (200/409 in body) and catch (HTTP 409).
   *
   * Uses valid_attorneys / invalid_attorneys as source of truth per attorney.
   * Enriches valid attorneys with TM counts from details[] if available.
   */
  const processResponse = (filtered, responseData) => {
    const data = responseData?.data || {};
    const apiMessage = responseData?.message || "";

    const validSet = new Set(data.valid_attorneys || []);
    const invalidSet = new Set(data.invalid_attorneys || []);

    // Build TM count lookup from details (may be empty for "Already Exist" responses)
    const detailsMap = {};
    const nameOptions = {};
    (data.details || []).forEach((d) => {
      detailsMap[String(d.attorney_number)] = Number(d.count || 0);
      if (d.attorney_names?.length) {
        nameOptions[String(d.attorney_number)] = d.attorney_names;
      }
    });

    const validation = {};
    const validationMessages = {};
    const tmCounts = {};

    filtered.forEach((num) => {
      if (validSet.has(num)) {
        // ✅ Valid — attorney exists and is available
        const count = detailsMap[num] ?? 0;
        validation[num] = "valid";
        tmCounts[num] = count;
        validationMessages[num] = `Valid - ${count} TMs`;
      } else if (invalidSet.has(num)) {
        // ❌ Invalid — attorney number not found
        validation[num] = "invalid";
        tmCounts[num] = 0;
        validationMessages[num] = "Invalid Attorney Number";
      } else {
        // ⚠️ Not in either array — already assigned to another user
        validation[num] = "invalid";
        tmCounts[num] = 0;
        validationMessages[num] = apiMessage || "Attorney Number already assigned";
      }
    });

    return { validation, validationMessages, tmCounts, nameOptions };
  };

  const handleCheckAttorneyStatus = async (attorneyNumbers) => {
    const filtered = attorneyNumbers
      .map((v) => v.trim())
      .filter((v) => v !== "");

    if (filtered.length === 0) {
      toast.warning("Please enter at least one attorney number.");
      return;
    }

    const query = filtered.join(",");

    try {
      setIsCheckingAttorney(true);

      const res = await axios.get(
        `${API_ELSTIC_URL}/attorney/get-attorney-status-count?attorney_number=${query}`,
      );

      const { status, message } = res.data;

      if (status === 200 || status === 409) {
        const { validation, validationMessages, tmCounts, nameOptions } =
          processResponse(filtered, res.data);

        // Merge (don't replace) so existing attorneys in edit mode aren't lost
        setAttorneyValidation((prev) => ({ ...prev, ...validation }));
        setAttorneyValidationMessages((prev) => ({ ...prev, ...validationMessages }));
        setAttorneyTMCounts((prev) => ({ ...prev, ...tmCounts }));
        if (setAttorneyNameOptions) setAttorneyNameOptions((prev) => ({ ...prev, ...nameOptions }));

        // Toast feedback
        const validCount = filtered.filter((n) => validation[n] === "valid").length;
        const invalidCount = filtered.length - validCount;

        if (validCount === filtered.length) {
          toast.success(message || "All attorney numbers verified successfully!");
        } else if (validCount > 0) {
          toast.success(`${validCount} of ${filtered.length} attorney numbers verified`);
          if (invalidCount > 0) {
            toast.warning(`${invalidCount} attorney number(s) are invalid or already assigned`);
          }
        } else {
          toast.warning(message || "No valid attorney numbers found");
        }
      }
    } catch (error) {
      console.error("Error checking attorney status:", error);

      if (error.response?.status === 409) {
        const responseData = error.response.data;

        const { validation, validationMessages, tmCounts, nameOptions } =
          processResponse(filtered, responseData);

        // Merge (don't replace)
        setAttorneyValidation((prev) => ({ ...prev, ...validation }));
        setAttorneyValidationMessages((prev) => ({ ...prev, ...validationMessages }));
        setAttorneyTMCounts((prev) => ({ ...prev, ...tmCounts }));
        if (setAttorneyNameOptions) setAttorneyNameOptions((prev) => ({ ...prev, ...nameOptions }));

        // Toast feedback
        const validCount = filtered.filter((n) => validation[n] === "valid").length;
        const invalidCount = filtered.length - validCount;

        if (validCount > 0 && invalidCount > 0) {
          toast.success(`${validCount} attorney(s) verified`);
          toast.warning(
            responseData.message || `${invalidCount} attorney number(s) are invalid or already assigned`,
          );
        } else if (invalidCount > 0) {
          toast.warning(
            responseData.message || "Attorney number(s) are invalid or already assigned",
          );
        }
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to check attorney status.";
        toast.error(errorMessage);
      }
    } finally {
      setIsCheckingAttorney(false);
    }
  };

  return { handleCheckAttorneyStatus };
};
